import { effect, inject } from '@angular/core';

import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { distinctUntilChanged, EMPTY, finalize, pipe, switchMap, timer } from 'rxjs';

import { environment } from '@env';

import { AuthStore } from '@core/auth';
import { get, set, STORAGE_KEYS } from '@core/storage';

import { SEARCH_RECENT } from './header/search/search.mock';
import { SEARCH_DEBOUNCE_MS, type SearchHit } from './header/search/search.types';
import { EMPTY_SHELL_NAV_COUNTS, type ShellNavNotificationCounts } from './shell-nav.types';
import { ShellService } from './shell.service';

function readMenuState(): boolean {
  return get(STORAGE_KEYS.shell.menuState) === 'true';
}

function writeMenuState(isIconMode: boolean): void {
  set(STORAGE_KEYS.shell.menuState, String(isIconMode));
}

export const ShellStore = signalStore(
  { providedIn: 'root' },

  environment.withDevtools('shell'),

  withState({
    isIconMode: readMenuState(),
    navNotificationCounts: EMPTY_SHELL_NAV_COUNTS,
    searchHits: null as readonly SearchHit[] | null,
    loading: false,
    searchRecents: SEARCH_RECENT,
  }),

  withMethods((store) => {
    const shellService = inject(ShellService);

    const search = rxMethod<string>(
      pipe(
        distinctUntilChanged(),
        switchMap((raw) => {
          const query = raw.trim();
          patchState(store, { searchHits: null, loading: Boolean(query) });
          if (!query) return EMPTY;

          return timer(SEARCH_DEBOUNCE_MS).pipe(
            switchMap(() => shellService.search(query)),
            tapResponse({
              next: (searchHits) => patchState(store, { searchHits }),
              error: () => patchState(store, { searchHits: [] }),
            }),
            finalize(() => patchState(store, { loading: false })),
          );
        }),
      ),
    );

    return {
      toggleIconMode(): void {
        const next = !store.isIconMode();
        patchState(store, { isIconMode: next });
        writeMenuState(next);
      },

      setNavNotificationCounts(
        counts: Pick<ShellNavNotificationCounts, 'home' | 'documents'>,
      ): void {
        patchState(store, {
          navNotificationCounts: {
            ...store.navNotificationCounts(),
            home: counts.home,
            documents: counts.documents,
          },
        });
      },

      clearNavNotificationCounts(): void {
        patchState(store, { navNotificationCounts: EMPTY_SHELL_NAV_COUNTS });
      },

      search,

      clearSearchRecents(): void {
        patchState(store, { searchRecents: [] });
      },

      resetSessionScopedState(): void {
        search('');
        patchState(store, {
          navNotificationCounts: EMPTY_SHELL_NAV_COUNTS,
          searchHits: null,
          loading: false,
          searchRecents: [],
        });
      },
    };
  }),

  withHooks({
    onInit(store): void {
      const authStore = inject(AuthStore);
      let previousKey = authStore.authorizationContextKey();

      effect(() => {
        const contextKey = authStore.authorizationContextKey();
        if (contextKey === previousKey) return;

        previousKey = contextKey;
        store.resetSessionScopedState();
      });
    },
  }),
);
